import { useState } from "react";
import { Alert, Button, Col, Modal, Row, Spinner } from "react-bootstrap";
import FlatPicker from "react-flatpickr";
import moment from "moment";
import { getDefaultDateForFlightDateTimePicker, isIfrFlightRules } from "../../../models/flight";
import FlightPlan, { getDisplayableTextFromFlightPlanPoint } from "../../../models/flightPlan";
import { useAppDispatch } from "../../../store/hooks";
import { duplicateFlightPlan, reverseFlightPlan } from "../../../store/flightPlansSlice";
import { useNavigate } from "react-router-dom";

interface DuplicateFlightPlanModalProps {
    flightPlan: FlightPlan;
    mode: "duplicate" | "reverse";
    onClose: () => void;
}

function DuplicateFlightPlanModal({ flightPlan, mode, onClose }: DuplicateFlightPlanModalProps) {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [departureDateTime, setDepartureDateTime] = useState<Date>(getDefaultDateForFlightDateTimePicker());
    const [isCreating, setIsCreating] = useState(false);
    const [error, setError] = useState<string | undefined>();

    const isReverse = mode === "reverse";

    const from = getDisplayableTextFromFlightPlanPoint(flightPlan.departure);
    const to = getDisplayableTextFromFlightPlanPoint(flightPlan.destination);
    const newFrom = isReverse ? to : from;
    const newTo = isReverse ? from : to;

    //Only a reversal loses the routing: the outbound Field 15 is not valid backwards, so it is dropped
    //server-side and the new plan starts unplanned. A duplicate keeps it (same pairing, same direction).
    const losesIfrRoute = isReverse && isIfrFlightRules(flightPlan.flightRules) && (flightPlan.ifpsRoute?.length ?? 0) > 0;

    //Both pickers work in the display frame, then convert once — each owns half the result, so editing
    //the time can never move the date (and vice versa) the way a mixed-frame compose does.
    const utcOffset = moment(departureDateTime).utcOffset();
    const pickerDate = moment(departureDateTime).subtract(utcOffset, "minutes").toDate();
    const fromDisplayFrame = (displayDate: Date) => moment(displayDate).add(utcOffset, "minutes").toDate();

    const handleCreate = async () => {
        setIsCreating(true);
        setError(undefined);
        try {
            const action = isReverse ? reverseFlightPlan : duplicateFlightPlan;
            const newPlan = await dispatch(
                action({ flightPlanId: flightPlan.id!, departureDateTime })
            ).unwrap();
            onClose();
            navigate(`/fpls/${newPlan.id}`);
        } catch (e: any) {
            setError(typeof e === "string" ? e : e?.message ?? `Failed to ${mode} flight plan.`);
            setIsCreating(false);
        }
    };

    return (
        <Modal show onHide={onClose}>
            <Modal.Header>
                <p className="h5">{isReverse ? "Reverse Flight Plan" : "Duplicate Flight Plan"}</p>
                <Button variant="close" onClick={onClose} />
            </Modal.Header>
            <Modal.Body>
                <Row className="gy-3">
                    <Col xs={12}>
                        <strong>Route:</strong> {newFrom} &rarr; {newTo}
                    </Col>
                    <Col xs={12}>
                        <strong>Aircraft:</strong> {flightPlan.aircraftRegistration}
                    </Col>
                    {losesIfrRoute && (
                        <Col xs={12}>
                            <Alert variant="warning" className="mb-0 py-2 px-3">
                                <small>
                                    The IFR route is <strong>not reversed</strong> — airways are often one-way and
                                    cruising levels flip with the direction, so the return leg has to be planned on its
                                    own. The new plan keeps everything else; plan its route before filing.
                                </small>
                            </Alert>
                        </Col>
                    )}
                    <Col xs={12}>
                        <FlatPicker
                            value={pickerDate}
                            onClose={(s) => {
                                if (s.length > 0) {
                                    setDepartureDateTime(
                                        fromDisplayFrame(
                                            new Date(
                                                s[0].getFullYear(),
                                                s[0].getMonth(),
                                                s[0].getDate(),
                                                pickerDate.getHours(),
                                                pickerDate.getMinutes()
                                            )
                                        )
                                    );
                                }
                            }}
                            options={{
                                dateFormat: "d-m-Y",
                                static: true,
                            }}
                            className="form-control bg-white"
                            render={({ value }, ref) => {
                                return (
                                    <div className="input-container rounded-lg" style={{ backgroundColor: "#F7F7F7" }}>
                                        <input ref={ref} className="w-100 bg-transparent" style={{ cursor: "pointer" }} readOnly />
                                        <label className={value ? "filled" : ""}>Departure Date (UTC)</label>
                                    </div>
                                );
                            }}
                        />
                    </Col>
                    <Col xs={12}>
                        <FlatPicker
                            value={pickerDate}
                            onClose={(s) => {
                                if (s.length > 0) {
                                    setDepartureDateTime(
                                        fromDisplayFrame(
                                            new Date(
                                                pickerDate.getFullYear(),
                                                pickerDate.getMonth(),
                                                pickerDate.getDate(),
                                                s[0].getHours(),
                                                s[0].getMinutes()
                                            )
                                        )
                                    );
                                }
                            }}
                            options={{
                                enableTime: true,
                                noCalendar: true,
                                dateFormat: "H:i",
                                time_24hr: true,
                                minuteIncrement: 5,
                                static: true,
                            }}
                            className="form-control bg-white"
                            render={({ value }, ref) => {
                                return (
                                    <div className="input-container rounded-lg" style={{ backgroundColor: "#F7F7F7" }}>
                                        <input ref={ref} className="w-100 bg-transparent" style={{ cursor: "pointer" }} readOnly />
                                        <label className={value ? "filled" : ""}>Departure Time (UTC)</label>
                                    </div>
                                );
                            }}
                        />
                    </Col>
                    {error && (
                        <Col xs={12}>
                            <small className="text-danger">{error}</small>
                        </Col>
                    )}
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={isCreating}>
                    Cancel
                </Button>
                <Button variant="dark" onClick={handleCreate} disabled={isCreating}>
                    {isCreating ? <Spinner animation="border" size="sm" /> : "Create"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default DuplicateFlightPlanModal;
